import React, { useCallback, useContext, useEffect, useState } from "react";
import { Container, PageHeading } from "@ui-components/Container";
import { FormattedMessage, useIntl } from "react-intl";
import Placeholder from "@ui-components/Placeholder";
import BottomBar from "@components/BottomBar";
import Button from "@ui-components/Button";

import {
  Discount,
  QuotationCreateSchema,
  QuotationProductEdit,
  QuotationDraftProps,
  QuotationDraftCreateSchema,
  QuotationWithDraftsSchema,
  DiscountRule1Props,
} from "../types";
import { computeTotals, generateQuotationCreatePayload } from "./lib/utilities";
import { QuotationProduct } from "../quotation-product/quotation-product";
import { formatCurrency, formatPercentage } from "./lib/format";
import { useQuotations } from "../hooks/useQuotations";
import CountryContext from "../../../CountryContext";
import { useHistory } from "react-router-dom";
import {
  PlusIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  XCircleIcon,
  XMarkIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { NotificationsContext } from "@ui-components/Notifications";
import AuthenticationService from "@services/AuthenticationService";
import { SelectProductsModal } from "@feature/quotations/select-products-modal";
import { EditProductDiscountModal } from "@feature/quotations/edit-product-discount-modal";
import Alert from "@ui-components/Alert";
import { AddProductModal } from "../add-product-modal";
import { WorksiteInfo } from "../worksite-info/worksite-info";
import { useWorksiteUsers } from "@hooks/useWorksiteUsers";
import _ from "lodash";
import {
  ApproveQuotationModal,
  DeclineQuotationModal,
} from "@feature/quotations";
import { UserReducedSchema } from "@feature/types";
import NotificationPing from "@ui-components/NotificationPing";
import { downloadBlob } from "../../commons/lib/utils";
import { SendToEnterpriseModal } from "../sent-to-enterprise-modal";
import { inviteExternalEnterprise } from "../lib/utilities";
import getKColorResultContextKey from "../../../utils/getKColorResultContextKey";

export const QuotationDraft = ({
  data, // the quotation data (if undefined the quotation will be created)
  worksite,
  products,
  setProducts,
  agent,
  enterprise,
  resale,
  surveys,
  loading = false,
  editable = false,
  fullScreen = false,
  historyBackPath = null,
  onHistoryBackClick = () => {},
  onSave = undefined,
  copyQuotation = false,
  agentId,
  quotationProducts,
  showVisibilityDisclaimer = false,
  notes = undefined,
  draftId,
  baseDraftInfo,
  enterpriseToBeInvited,
  kColorPriceChoose
}: QuotationDraftProps) => {

  // context
  const {
    country: { code },
  } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const history = useHistory();
  const {
    createQuotation,
    updateQuotation,
    loading: submitting,
    downloadQuotationPdf,
    downloadingPdf,
    sendToEnterprise,
    sendingToEnterprise,
    sendDraftResponse,
    createQuotationDraft,
  } = useQuotations();
  const [worksiteUsers] = useWorksiteUsers(worksite);

  // state
  const [edits, setEdits] = useState<{ [key: string]: QuotationProductEdit }>(
    {}
  );
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [multipleDiscountCodes, setMultipleDiscountCodes] =
    useState<string[]>();
  const [approvalModal, setApprovalModal] = useState<
    "approve" | "decline" | "closed"
  >("closed");
  const [invitingEnterprise, setInvitingEnterprise] = useState(false);
  // used to keep track of the products added in the current session
  const [addedProductsCodes, setAddedProductsCodes] = useState<string[]>();
  const [originalEdits, setOriginalEdits] = useState<{
    [key: string]: QuotationProductEdit;
  }>({});
  const [showSendToEnterpriseModal, setShowSendToEnterpriseModal] =
    useState(false);

  // utilities
  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();
  const currentDraft = data?.drafts?.find((d) => d.id === draftId);
  const showResaleAcceptRefuseButtons =
    // resale exists
    !!data?.id_resale &&
    // user is the resale
    [userData.id, userData.account_user_id].includes(data?.id_resale) &&
    // resale has permissions
    data?.r_perm.indexOf(`user_${data?.id_resale}`) >= 0 &&
    // draft is the latest made
    currentDraft?.status === "latest" &&
    baseDraftInfo?.id === currentDraft?.id &&
    // the resale has not created a single draft.
    baseDraftInfo?.message === "latest_agent_draft" &&
    // not created by the resale
    currentDraft?.id_creator !== data?.id_resale;
  const canAddProduct = userScopes.includes("quotation:add_product");


  const allKcolorPricesSet = !!products && Object.values(products)
    .filter(p => p.isKcolor)
    .map(p => p.product.prezzo)
    .every(price => price !== null && price !== undefined);
  const productsReducer = (
    acc: { [key: string]: QuotationProductEdit },
    curr: string
  ) => ({
    ...acc,
    // if the product is already in the edits, keep the previous values
    [curr]: edits[curr]
      ? {
          checked: edits[curr].checked,
          quantity: edits[curr].quantity,
          discount: edits[curr].discount,
        }
      : // otherwise, create a new entry
      /*
    if the user is an enterprise, there are no edits
    (otherwise they would be shown in red)
    but the product price is already discounted
    and the quantity is already updated
    so the product technical sheets shows proper values
     */
      userData.role_type === "enterprise"
      ? {
          checked: true,
          quantity: null,
          discount: { type: "no_discount" } as Discount,
        }
      : /*
      if the user is not an enterprise, the edits stored in
      the table quotation_products are used so that the last
      version of the product is shown.
       */
        {
          checked: true,
          // use product updatedQuantity in order to show the last edited quantity
          quantity:
            quotationProducts?.find((qp) => qp.cod_product === curr || curr === getKColorResultContextKey(
              qp.products!!.code,
              qp.kcolor_color_code,
              qp.products!!.min_qty!,
              ))?.updatedQuantity || null,
          discount: quotationProducts?.find(
            (qp) => qp.cod_product === curr || curr === getKColorResultContextKey(
              qp.products!!.code,
              qp.kcolor_color_code,
              qp.products!!.min_qty!,
            )
          )
            ? // if the product has a discount, use it
              ({
                type: quotationProducts.find(
                  (qp) => qp.cod_product === curr || curr === getKColorResultContextKey(
                    qp.products!!.code,
                    qp.kcolor_color_code,
                    qp.products!!.min_qty!
                  ))?.val_product_discountruletype,
                value: quotationProducts.find(
                  (qp) => qp.cod_product === curr || curr === getKColorResultContextKey(
                    qp.products!!.code,
                    qp.kcolor_color_code,
                    qp.products!!.min_qty!
                  ))?.val_product_total_discountrule,
              } as Discount)
            : // otherwise, use a no_discount discount
              ({
                type: "no_discount",
              } as Discount),
        },
  });
  const getDiscountMarginalityMessage = () => {
    if (userScopes.includes("quotation:apply_discount"))
      return {
        id: "apply_multiple_discount",
        defaultMessage: "Applica sconto multiplo",
      };
    if (userScopes.includes("quotation:apply_marginality"))
      return {
        id: "apply_multiple_marginality",
        defaultMessage: "Applica marginalità multipla",
      };
    return null;
  };
  // is a new quotation?
  const isNewQuotation = data === undefined;

  const atLeastOneProductEdited = (edits: {
    [key: string]: QuotationProductEdit;
  }) => {
    return !_.isEqual(edits, originalEdits);
  };

  const atLeastOneProductAdded = (edits: {
    [key: string]: QuotationProductEdit;
  }) => {
    return Object.keys(edits).some(
      (k) => addedProductsCodes?.includes(k) && edits[k].checked
    );
  };

  const allProductsDisabled =
    !!products &&
    Object.keys(products).every((k) => {
      const key = parseInt(k);
      return key in edits && !edits[key].checked;
    });
  const emptyProducts = !!products && Object.keys(products).length === 0;
  const renderProducts = () => {
    let content;
    if (!products)
      content = Array(3)
        .fill(null)
        .map((_, idx) => (
          <Placeholder key={idx} width="w-full" height="h-48" />
        ));
    else if (emptyProducts)
      content = (
        <div className="p-20 flex items-center justify-center text-xl text-gray-200 font-semibold">
          <FormattedMessage id="no_available_products" />
        </div>
      );
    else {
      const productsArray = Object.entries(products);

      const kColorProducts = productsArray.filter(([, product]) => product.isKcolor)

      const otherProducts = productsArray.filter(([, product]) => !product.isKcolor);

      const atLeastOneKcolorProductPriceHasBeenSetByTintometer = () => {
        Object.entries(kColorProductsPriceUnSet).some(([_, value]) => !value);
      }

      const renderProducts = (products, title = "") => (
        <div>
          {title && <h1 className="text-lg font-bold my-8">{title}</h1>}
          {products.map(([code, product], idx) => (
            <React.Fragment key={code}>
              <QuotationProduct
                posix={idx}
                editable={editable && (
                  ![null, undefined].includes(product.product.prezzo)
                  || userData.role_type === "resale_tintometer"
                )}
                productInfo={product}
                productEdits={edits[code]}
                setProductEdits={(edit) =>
                  setEdits((prev) => ({
                    ...prev,
                    [code]: edit,
                  }))
                }
                originalProductEdits={originalEdits[code]}
                onCheck={
                  editable
                    ? () =>
                        setEdits((prev) => ({
                          ...prev,
                          [code]: {
                            ...prev[code],
                            checked: !prev[code].checked,
                          },
                        }))
                    : undefined
                }
                setProducts={setProducts}
              />
            </React.Fragment>
          ))}
        </div>
      );

      content = (
        <div>
          {renderProducts(otherProducts)}
          {kColorProducts.length > 0 &&
            renderProducts(
              kColorProducts,
              intl.formatMessage({
                id: "kcolor_products",
                defaultMessage: "Prodotti K-Colors",
              })
            )}
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-8">
          <h3 className="text-xl font-semibold">
            <FormattedMessage id="products_list" />
          </h3>
          <div className="flex flex-col lg:flex-row lg:items-center gap-2">
            {!emptyProducts &&
              editable &&
              ["apply_marginality", "apply_discount"].some((ps) =>
                userScopes.includes(`quotation:${ps}`)
              ) && (
                <>
                  <Button
                    full
                    onClick={() => {
                      setMultipleDiscountCodes([]);
                    }}
                  >
                    <FormattedMessage {...getDiscountMarginalityMessage()} />
                  </Button>
                </>
              )}
            {editable && canAddProduct &&(
              <>
                {/* @ts-ignore */}
                <Button
                  full
                  onClick={() => {
                    setShowAddProduct(true);
                  }}
                  styleType="darkGreen"
                >
                  <PlusIcon className="w-5 mr-1" />
                  <FormattedMessage
                    id="add_product"
                    defaultMessage="Aggiungi un prodotto"
                  />
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col">{content}</div>
      </>
    );
  };
  const getTotals = () => {
    if (!products) return null;

    return Object.entries(edits)
      .filter(([_, { checked }]) => checked)
      .map(([code, { discount, quantity: updatedQuantity }]) => {
        const {
          product: { prezzo: price, min_qty },
          quantity,
        } = products[code];
        return computeTotals(
          quantity,
          min_qty,
          price,
          discount,
          updatedQuantity
        );
      })
      .reduce(
        (acc, curr) => ({
          original: acc.original + curr.original,
          discounted: acc.discounted + curr.discounted,
        }),
        { original: 0, discounted: 0 }
      );
  };
  const renderTotal = () => {
    const values = getTotals();
    const totals = [
      {
        key: editable ? "total_quotation" : "total_cost",
        value: values?.discounted,
        reduction: null as number | null,
        bold: !editable,
      },
    ];
    if (editable)
      totals.push({
        key: "total_discounted",
        value: values?.discounted,
        reduction: !!values
          ? (values.discounted - values.original) / values.original
          : null,
        bold: true,
      });

    return totals.map(({ key, value, reduction, bold }, idx, arr) => {
      const cls = bold ? "text-base sm:text-lg font-semibold" : "text-xs";
      return (
        <React.Fragment key={idx}>
          <span className={cls}>
            <FormattedMessage id={key} />
          </span>
          <div className={cls}>
            {!!value || value === 0 ? (
              <span>
                {formatCurrency(value)}
                {!!reduction && (
                  <span className="text-red-600 ml-2">
                    ({formatPercentage(reduction)})
                  </span>
                )}
              </span>
            ) : (
              <Placeholder
                width="w-32"
                height={arr.length > 1 && idx === 0 ? "h-3" : "h-5"}
                marginClass={arr.length > 1 && idx === 0 ? "my-0.5" : "my-1.5"}
              />
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  const renderActions = () => {
    // If in edit mode, only show the save button
    if (editable)
      return (
        <>
          <Button
            full
            big
            disabled={
              (!isNewQuotation && !allKcolorPricesSet) || // For existing quotations, all k-color products must have their price set
              emptyProducts || // There are no products
              allProductsDisabled || // All products are unselected
              submitting || // The form is already being submitted // The quotation is not new (other drafts exist) and no product has been edited nor added
              (!isNewQuotation &&
                !(
                  atLeastOneProductEdited(edits) ||
                  atLeastOneProductAdded(edits)
                ))
            }
            onClick={() => submit()}
            submitting={submitting || invitingEnterprise}
          >
            <FormattedMessage
              id={copyQuotation ? "copy" : "save_draft"}
              defaultMessage={
                copyQuotation ? "copia preventivo" : "salva bozza"
              }
            />
          </Button>
        </>
      );

    // If in view mode, show buttons based on the user
    return (
      <div className="flex flex-col sm:flex-row items-center gap-3">
        {/* --- DOWNLOAD BUTTON: only visible if the quotation exists (always?!) */}
        {!isNewQuotation && (
          <Button
            full
            submitting={downloadingPdf}
            onClick={async () => {
              const result: Blob = await downloadQuotationPdf(data.id, draftId);
              downloadBlob(
                result,
                intl.formatMessage({
                  id: "quotation_pdf_filename",
                  defaultMessage: `Preventivo ${data.id}.pdf`,
                })
              );
            }}
            containerClassName="w-full"
          >
            <ArrowDownTrayIcon className="w-5 mr-1" />
            <FormattedMessage id={"download"} />
          </Button>
        )}
        {[userData.role_type, userData.account_user_role_type].includes("enterprise") &&
          data?.status === "waiting_for_enterprise" &&
          currentDraft?.status === "sent" && (
            <>
              <Button
                full
                styleType="darkGreen"
                onClick={() => setApprovalModal("approve")}
                containerClassName="w-full"
              >
                <CheckCircleIcon className="w-5 mr-1" />
                <FormattedMessage id="accept" defaultMessage="Accetta" />
              </Button>
              <Button
                full
                styleType="red"
                onClick={() => setApprovalModal("decline")}
                containerClassName="w-full"
              >
                <XCircleIcon className="w-5 mr-1" />
                <FormattedMessage id="refuse" defaultMessage="Rifiuta" />
              </Button>
            </>
          )}
        {showResaleAcceptRefuseButtons && (
          <>
            <Button
              full
              styleType="darkGreen"
              onClick={() => {
                history.push(`/${code}/quotations/${data!.id}/drafts/new`);
              }}
              containerClassName="w-full"
            >
              <CheckCircleIcon className="w-5 mr-1" />
              <span className="whitespace-nowrap">
                <FormattedMessage
                  id="accept_and_proceed"
                  defaultMessage="Accetta e modifica"
                />
              </span>
            </Button>
            <Button
              full
              styleType="red"
              onClick={() => setApprovalModal("decline")}
              containerClassName="w-full"
            >
              <XCircleIcon className="w-5 mr-1" />
              <FormattedMessage id="refuse" defaultMessage="Rifiuta" />
            </Button>
          </>
        )}
        {/* --- EXIT BUTTON: always visible to everybody */}
        <Button
          full
          onClick={() => {
            if (historyBackPath) history.push(historyBackPath);
            else onHistoryBackClick();
          }}
          containerClassName="w-full"
        >
          <XMarkIcon className="w-5 mr-1" />
          <FormattedMessage id="exit" />
        </Button>
      </div>
    );
  };


  useEffect(() => {
    if (!products) return;
    if (Object.keys(originalEdits).length > 0) return;
    const e = Object.keys(products).reduce(productsReducer, {});
    setEdits(e);
    setOriginalEdits(_.cloneDeep(e));
  }, [products]);

  useEffect(() => {
    if (!fullScreen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [fullScreen]);

  const hasKcolorProducts = useCallback(() => {
    if(!products) return false;
    return (
      Object.values(products).some((p) => p.isKcolor)
    );
  }, [products]);

  const submit = async () => {
    if (!editable) throw new Error("Can't submit a non-editable quotation");

    if (!products) throw new Error("Data not loaded yet");

    const totals = getTotals();

    if (isNewQuotation && copyQuotation && !agent) {
      const agentFromWorksiteUsers = (
        worksiteUsers as UserReducedSchema[]
      ).find((u) => u.id === agentId);
      if (!agentFromWorksiteUsers) throw new Error("Agent not found");
      agent = agentFromWorksiteUsers;
    }

    if (!totals || (isNewQuotation && !agent)) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      return;
    }

    if (isNewQuotation) {
      // Create new quotation
      const quotation = await createQuotation({
        id_agent: agent.id,
        des_agent: agent.name,
        id_enterprise: enterprise?.account_user_id || enterprise?.id || null,
        des_enterprise: (enterprise?.account_user_company ?? enterprise?.account_user_name ?? enterprise?.company ?? enterprise?.name) || null,
        id_resale: resale?.account_user_id || resale?.id || null,
        des_resale: (resale?.account_user_company ?? resale?.account_user_name ?? resale?.company ?? resale?.name) || null,
        id_worksite: worksite!.id,
        val_total: totals.discounted, // now saving the discounted value to display it in the list
        products: generateQuotationCreatePayload(products, edits, kColorPriceChoose, userData),
        survey_ids: surveys?.map(({ id }) => id) || [],
        kcolor_chosen_price: kColorPriceChoose,
      } as QuotationCreateSchema);

      if (enterpriseToBeInvited) {
        setInvitingEnterprise(true);
        await inviteExternalEnterprise(
          enterpriseToBeInvited,
          quotation,
          updateQuotation,
          code,
          push,
          intl,
          undefined,
          false
        );
        setInvitingEnterprise(false);
      }
      history.push(`/${code}/quotations/${quotation.id}`);
    } else {
      // Create a new draft for the existing quotation
      const payload = {
        id_quotation: data.id,
        products: generateQuotationCreatePayload(products, edits, kColorPriceChoose, userData),
        val_total: totals.discounted, // now saving the discounted value to display it in the list
      } as QuotationDraftCreateSchema;
      const updated: QuotationWithDraftsSchema = await createQuotationDraft(
        payload
      );
      onSave!(updated);
    }
  };
  const Wrapper = ({ children }: { children?: React.ReactNode }) => {
    if (!fullScreen) return <>{children}</>;
    return (
      <div className="fixed bg-white inset-0 z-50 animate-slideLeftIn overflow-y-auto">
        {children}
      </div>
    );
  };

  return (
    <>
      <Wrapper>
        <Container>
          <PageHeading
            title={<FormattedMessage id="quotation_long" />}
            titleClasses={"text-xl"}            // @ts-ignore
            historyBackPath={historyBackPath}
            // @ts-ignore
            onHistoryBackClick={onHistoryBackClick}
          />
          <div className="mb-6 sm:mt-4 flex flex-col gap-10 min-h-screen">
            <div className="flex flex-col gap-4">
              {/* @ts-ignore */}
              <Alert
                type="warning"
                title={intl.formatMessage({
                  id: "disclaimer",
                  defaultMessage: "Disclaimer",
                })}
                text={intl.formatMessage({
                  id: "quotation_disclaimer",
                  defaultMessage:
                    "Il preventivo ha valore di massima, puramente indicativo, non essendo in alcun modo vincolante per Kerakoll S.p.A. I prezzi ivi riportati sono al netto dell’IVA e corrispondenti al listino in vigore al momento della formulazione del presente documento, come tali suscettibili di possibili variazioni. Una valorizzazione più precisa, unitamente all’indicazione delle ulteriori condizioni di vendita (condizioni di pagamento, disponibilità del materiale, resa della merce ecc.) verrà effettuata, da parte di Kerakoll S.p.A., al momento dell’elaborazione dell’offerta commerciale a Voi riservata.",
                })}
              />
              {!!showVisibilityDisclaimer && (
                // @ts-ignore
                <Alert
                  type="info"
                  text={intl.formatMessage({
                    id: "quotation_visibility_disclaimer",
                    defaultMessage:
                      "Solamente tu puoi vedere le operazioni che hai effettuato su questa bozza. Gli altri utenti ne vedranno solamente i valori finali.",
                  })}
                />
              )}
            </div>
            <WorksiteInfo worksite={worksite} quotation={data} agent={agent} />
            {!!notes && (
              <div className="border bg-gray-50 p-5 flex flex-col gap-3">
                <p className="font-semibold flex flex-row items-center gap-3">
                  <NotificationPing className="mt-0.5" />
                  <FormattedMessage
                    id="enterprise_notes_title"
                    defaultMessage="Messaggio lasciato dall'impresa"
                  />
                </p>
                <p className="text-base">{notes}</p>
              </div>
            )}
            {renderProducts()}
          </div>
        </Container>
        <BottomBar>
          <div className="flex flex-col md:items-end lg:flex-row lg:items-center lg:justify-end gap-4 py-4">
            <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8">
              {renderTotal()}
            </div>
            {renderActions()}
          </div>
        </BottomBar>
      </Wrapper>

      {/* -------------------- MODALS --------------------*/}

      {/* Enterprise approval modals */}
      {approvalModal == "approve" && (
        <ApproveQuotationModal
          onConfirm={async () => {
            await sendDraftResponse(draftId!, "accepted", undefined);
            history.push(`/${code}/quotations/${data!.id}`);
            setApprovalModal("closed");
          }}
          onDismiss={() => {
            setApprovalModal("closed");
          }}
          loading={submitting}
        />
      )}
      {approvalModal == "decline" && (
        <DeclineQuotationModal
          onConfirm={async (choice, message) => {
            const status =
              choice === "request" ? "rejected_soft" : "rejected_hard";
            await sendDraftResponse(draftId!, status, message);
            setApprovalModal("closed");
            history.push(`/${code}/quotations/${data!.id}`);
          }}
          onDismiss={() => {
            setApprovalModal("closed");
          }}
          loading={submitting}
        />
      )}

      {/* Multiple discount modal */}
      {!!products && multipleDiscountCodes?.length === 0 && (
        <SelectProductsModal
          title={<FormattedMessage {...getDiscountMarginalityMessage()} />}
          products={products}
          onConfirm={(ids) => {
            setMultipleDiscountCodes(ids);
          }}
          onClose={() => {
            setMultipleDiscountCodes(undefined);
          }}
        />
      )}

      {/* Edit discount/quantity modal */}
      {!!multipleDiscountCodes && multipleDiscountCodes.length > 0 && (
        <EditProductDiscountModal
          selectedProducts={Object.entries(products || {})
            .filter(([key, _]) => multipleDiscountCodes.includes(key))
            .map(([_, p]) => p)}
          onConfirm={(d) => {
            setEdits((prev) => {
              const newEdits = { ...prev };
              multipleDiscountCodes.forEach((code) => {
                const prevDiscount = prev[code].discount;
                if (
                  !!prevDiscount?.value &&
                  prevDiscount.type === d.type &&
                  d.type === "discount_rule_1"
                ) {
                  // CASE A: a discount was already set and both new and old discounts are formula discounts
                  // --> Merge changes
                  const newDiscountValue = d.value as DiscountRule1Props;
                  let merged = {
                    ...(prevDiscount.value as DiscountRule1Props),
                    transport_eur_kg: newDiscountValue.transport_eur_kg,
                  } as DiscountRule1Props;
                  if (
                    !!d.value &&
                    userScopes.includes("quotation:apply_discount")
                  )
                    merged = {
                      ...merged,
                      ..._.pick(newDiscountValue, [
                        "discount_1",
                        "discount_2",
                        "discount_3",
                      ]),
                    };
                  if (userScopes.includes("quotation:apply_marginality"))
                    merged.marginality = newDiscountValue.marginality;
                  newEdits[code] = {
                    ...newEdits[code],
                    discount: { ...d, value: merged },
                  };
                }
                // CASE B: no discount was set or at least one of the discounts is not a formula discount
                // --> Overwrite changes
                else newEdits[code] = { ...newEdits[code], discount: d };
              });
              return newEdits;
            });
          }}
          onExit={() => {
            setMultipleDiscountCodes(undefined);
          }}
          editable={editable}
        />
      )}

      {/* Add product modal */}
      {showAddProduct && setProducts && surveys && (
        <AddProductModal
          surveys={surveys}
          onClose={() => {
            setShowAddProduct(false);
          }}
          onConfirm={(surveyProductItem) => {
            setProducts((prev) => ({
              ...prev,
              [surveyProductItem.product.art_cod]: {...surveyProductItem, isKcolor: false},
            }));
            setAddedProductsCodes((prev) => [
              ...(prev || []),
              surveyProductItem.product.art_cod,
            ]);
            setEdits((prev => ({
              ...prev,
              [surveyProductItem.product.art_cod]: {
                checked: true,
                quantity: null,
                discount: { type: "no_discount" } as Discount,
              }
            })));
            setShowAddProduct(false);
          }}
        />
      )}
    </>
  );
};

export default QuotationDraft;
